/**
 * The interaction states of the shared primitives that Mantine props cannot express: hover,
 * focus-visible and disabled, a decorative mark following the link that wraps it, and a hairline
 * between sibling rows. Everything else about these components is Mantine props and `styles`.
 *
 * Why a stylesheet string rather than a `.css.ts` (ADR-16): this package renders inside insights
 * fragments, whose HTML the portal injects, and a Vanilla Extract class compiled by one build does
 * not exist in the other's CSS. These rules key on `data-ui-*` attributes instead of classes and
 * read only Mantine's CSS variables, so the same text styles the markup whichever build produced
 * it. The portal renders {@link PrimitiveStyles} once at its root; the fragment layout renders it
 * inside `#fragment-root`, so it travels with the injected HTML (a duplicate is harmless).
 *
 * Mantine's style props are inline, and inline beats any rule here — so a property that changes
 * with state (the background) is set only here, never as a prop on the same element.
 */

const DARK = "[data-mantine-color-scheme='dark']";

const ARROW = '[data-ui-arrow]';
const CARD = '[data-ui-directory-card]';
const ROW = '[data-ui-directory-row]';
const ROW_ITEM = '[data-ui-directory-item]';

const FOCUS_RING = 'outline: 2px solid var(--mantine-color-blue-filled); outline-offset: 2px;';

/**
 * @desc The rules, as one stylesheet. Exported for a host that inlines CSS itself; React callers
 * render {@link PrimitiveStyles}.
 */
export const primitiveStyles = `
${ARROW} {
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  padding: 0; border: none; border-radius: 50%;
  background-color: var(--mantine-color-blue-filled); color: var(--mantine-color-white);
  cursor: pointer; text-decoration: none; transition: background-color 120ms ease;
  outline-offset: 2px;
}
${ARROW}:hover, a:hover ${ARROW}, button:hover ${ARROW}, ${ARROW}[data-preview-state='hover'] {
  background-color: var(--mantine-color-blue-filled-hover);
}
${ARROW}:focus-visible { outline: 2px solid var(--mantine-color-blue-filled); }
${ARROW}:disabled, ${ARROW}[aria-disabled='true'], ${ARROW}[data-disabled] {
  background-color: var(--mantine-color-gray-3); color: var(--mantine-color-gray-6);
  cursor: not-allowed;
}
${DARK} ${ARROW}:disabled, ${DARK} ${ARROW}[aria-disabled='true'], ${DARK} ${ARROW}[data-disabled] {
  background-color: var(--mantine-color-dark-4); color: var(--mantine-color-dark-2);
}
${CARD}, ${ROW} { transition: background-color 120ms ease; }
${CARD}:hover, ${ROW}:hover { background-color: var(--mantine-color-default-hover); }
${CARD}:focus-visible, ${ROW}:focus-visible { ${FOCUS_RING} }
${ROW_ITEM} + ${ROW_ITEM} { border-top: 1px solid var(--mantine-color-default-border); }
`;

/**
 * @desc Renders {@link primitiveStyles} as a plain `<style>` element — not React 19's hoisted
 * `<style href precedence>`, which would move it into `<head>`, outside the `#fragment-root` the
 * portal extracts from a fragment.
 */
export function PrimitiveStyles() {
  return <style data-ui-primitives dangerouslySetInnerHTML={{ __html: primitiveStyles }} />;
}
